import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiEdit2, FiDownload, FiTruck } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { exportExcel } from '../utils/exportExcel';

export default function Replenishment() {
  const perm = findPerm(useMe().modules, 'replenishment');
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [toOrderOnly, setToOrderOnly] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [toast, setToast] = useState('');

  async function load() { try { setRows(await api('/replenishment')); } catch (e) { setToast(e.message); } }
  useEffect(() => { load(); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function orderOnce(r) {
    if (!window.confirm(`Raise a purchase request for ${r.to_order} × ${r.name}?`)) return;
    try { await api(`/replenishment/${r.id}/order`, { method: 'POST', body: { qty: r.to_order } });
      flash(`Purchase request raised for ${r.to_order} × ${r.name}`); load(); }
    catch (e) { flash(e.message); }
  }

  const shown = (rows || []).filter((r) => !toOrderOnly || r.needs_order || r.needs_assembly);
  const orderCount = (rows || []).filter((r) => r.needs_order || r.needs_assembly).length;

  function doExport() {
    exportExcel('Replenishment', [
      { label: 'Product', key: 'name' },
      { label: 'Brand', value: (r) => r.brand_name || '' },
      { label: 'On hand', key: 'issuable_stock' },
      { label: 'Min', key: 'min_level' },
      { label: 'Max', key: 'max_level' },
      { label: 'Supply', value: (r) => (r.supply_method === 'make' ? 'Make' : 'Buy') },
      { label: 'To order', key: 'to_order' },
    ], shown);
  }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Inventory</div>
          <h1>Replenishment</h1>
          <div className="count">
            {rows ? `${rows.length} products` : 'Loading…'}
            {orderCount > 0 && <span className="pill" style={{ background: '#FBEEEA', color: '#B4432E', marginLeft: 8 }}>{orderCount} to replenish</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={doExport} disabled={!rows || rows.length === 0}><FiDownload /> Export</button>
          <button className="btn btn-sm" onClick={load}><FiRefreshCw /> Refresh</button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <button className={'btn btn-sm' + (toOrderOnly ? ' btn-dark' : '')} onClick={() => setToOrderOnly((v) => !v)}>
          {toOrderOnly ? 'Showing to-replenish' : 'To replenish only'}
        </button>
      </div>

      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         shown.length === 0 ? <div className="empty"><h3>{toOrderOnly ? 'Nothing to replenish' : 'No products'}</h3><p>{toOrderOnly ? 'All products are above their minimum.' : 'Add products in Products & stock.'}</p></div> :
          <table className="data">
            <thead><tr>
              <th>Product</th><th>Brand</th>
              <th style={{ textAlign: 'right' }}>On hand</th>
              <th style={{ textAlign: 'right' }}>Min</th>
              <th style={{ textAlign: 'right' }}>Max</th>
              <th>Supply</th>
              <th style={{ textAlign: 'right' }}>To order</th>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} style={(r.needs_order || r.needs_assembly) ? { background: '#FDF6F4' } : null}>
                  <td style={{ fontWeight: 600 }}>{r.name}</td>
                  <td style={{ color: 'var(--muted)' }}>{r.brand_name || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: (r.needs_order || r.needs_assembly) ? '#B4432E' : '#1E2733' }}>{r.issuable_stock}</td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{r.min_level || '—'}</td>
                  <td style={{ textAlign: 'right', color: 'var(--muted)' }}>{r.max_level || '—'}</td>
                  <td>
                    <span className="pill" style={r.supply_method === 'make'
                      ? { background: '#F0EAF5', color: '#6B4B7A' }
                      : { background: '#EAF0F8', color: '#3A6A96' }}>
                      {r.supply_method === 'make' ? 'Make' : 'Buy'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: r.to_order > 0 ? '#B08D57' : 'var(--muted)' }}>{r.to_order > 0 ? r.to_order : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="row-actions">
                      {perm.can_edit && <button className="btn btn-sm" title="Set min/max" onClick={() => setEditRow(r)}><FiEdit2 /></button>}
                      {r.needs_order && perm.can_create &&
                        <button className="btn btn-sm" style={{ color: '#017E84' }} onClick={() => orderOnce(r)}><FiTruck /> Order Once</button>}
                      {r.needs_assembly && perm.can_create &&
                        <button className="btn btn-sm" style={{ color: '#6B4B7A' }} title="This product is assembled" onClick={() => nav('/m/assembly')}><FiTruck /> Create Assembly</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {editRow && <LevelsModal row={editRow} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); flash('Levels updated'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function LevelsModal({ row, onClose, onSaved }) {
  const [min, setMin] = useState(row.min_level || '');
  const [max, setMax] = useState(row.max_level || '');
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  async function save() {
    setError(''); setBusy(true);
    try { await api(`/replenishment/${row.id}/levels`, { method: 'PATCH', body: { min_level: min === '' ? 0 : Number(min), max_level: max === '' ? 0 : Number(max) } }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Reorder levels</h2>
        <p className="page-desc" style={{ margin: '0 0 14px', fontSize: 13 }}>{row.name} · on hand {row.issuable_stock} · {row.supply_method === 'make' ? 'Assembled (make)' : 'Purchased (buy)'}</p>
        <div className="grid2">
          <div className="field"><label>Min (reorder point)</label>
            <input type="number" value={min} autoFocus onChange={(e) => setMin(e.target.value)} placeholder="Replenish when at/below this" /></div>
          <div className="field"><label>Max (fill up to)</label>
            <input type="number" value={max} onChange={(e) => setMax(e.target.value)} placeholder="Target level" /></div>
        </div>
        <p className="page-desc" style={{ fontSize: 12.5, marginTop: 4 }}>
          {row.supply_method === 'make'
            ? 'This product is assembled — when low, it suggests creating an assembly (not a purchase).'
            : 'This product is purchased — when low, it suggests raising a purchase request.'}
        </p>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
